import type { Language } from '@/shared/i18n'

const MS_PER_DAY = 86_400_000

export function today(): Date {
  const now = new Date()

  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export function parseDate(iso: string): Date | undefined {
  const [year, month, day] = iso.split('-').map(Number)

  if (!year || !month || !day) {
    return undefined
  }

  return new Date(year, month - 1, day)
}

export function daysUntil(iso: string, from: Date = today()): number | undefined {
  const date = parseDate(iso)

  if (!date) {
    return undefined
  }

  return Math.round((date.getTime() - from.getTime()) / MS_PER_DAY)
}

export function formatDay(iso: string, language: Language): string {
  const date = parseDate(iso)

  if (!date) {
    return iso
  }

  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(date)
}

export function formatFullDate(iso: string, language: Language): string {
  const date = parseDate(iso)

  if (!date) {
    return iso
  }

  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

export function formatMonth(iso: string, language: Language): string {
  const date = parseDate(iso)

  if (!date) {
    return iso
  }

  return new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(date)
}

export function pluralizeDays(days: number, language: Language): string {
  const count = Math.abs(days)

  if (language !== 'ru') {
    return count === 1 ? '1 day' : `${count} days`
  }

  const tens = count % 100
  const ones = count % 10

  if (tens >= 11 && tens <= 14) {
    return `${count} дней`
  }

  if (ones === 1) {
    return `${count} день`
  }

  if (ones >= 2 && ones <= 4) {
    return `${count} дня`
  }

  return `${count} дней`
}
